// The Optad360 static slot inside the Games (slot 1) and Win (slot 2) windows: the legacy markup,
// rendered once per window mount (legacy rebuilt the DOM on every open, so the vendor script already
// copes with a freshly inserted <ins>). Nothing else is called: the plugin picks it up by itself
// (verified on production 2026-10-07).
// The Win window's second box at the TOP (owner's ask 2026-10-15), rendered only when the screen has room
// (WinWindow decides): its own id, but data-slot 2 like the bottom one (owner 2026-10-17: the Optad360
// config lists only slots 1 and 2, so the slot 3 it had first and the Ranking's slot 4 stayed empty; the
// Ranking box was dropped). Optad360 then fills both Win boxes from the same `..._o3b_stat` unit.
//
// Box size (owner 2026-10-16): Optad360 serves two formats in these slots, a ~728x90 banner on desktop
// and a smaller mobile one, so the size lists stay the legacy ones. The box reserves the tallest
// creative that can come on this screen (--ad-slot-h) plus the vendor line ("Ads by optAd360"), so
// the window never jumps when the ad arrives and the ad is never squeezed.
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { adsEnabled } from './WindowFrame';

const SIZES_DESKTOP = '750x100,728x90,700x100';
const SIZES_MOBILE = '300x50,300x100,320x100,320x50,360x100,360x50';

/** The sizes of `list` no wider than `width` and no taller than `height`. */
const fitting = (list: string, width: number, height = Infinity) =>
  list
    .split(',')
    .filter((s) => Number(s.split('x')[0]) <= width && Number(s.split('x')[1]) <= height)
    .join(',');

/** The size lists for a box `width` px wide (ad audit 2026-10-18). Optad360 picks its desktop or mobile list by
 *  the SCREEN width (971+ = desktop), not by the window: a desktop browser window, or an iPad Pro window, narrower
 *  than the 700-750 px banners got one anyway and the box cut it off (an ad cut off or spilling over the window
 *  risks the account, .ai/ui-and-options.md). So each list keeps only the sizes that fit the box, and a desktop
 *  list with none falls back to the mobile sizes that fit. At 1280 x 900 both lists stay the legacy ones. */
export function sizesFor(width: number, height = Infinity): { desktop: string; mobile: string } {
  // no taller than the room saved for it (owner 2026-10-26: on a low iPhone a 100 px creative came into a box saved
  // for 50 px and the Games list had no room left; the vendor also reserves the TALLEST size of the list + 30 px, so
  // a list with 100 px sizes left a strip under a 90 px banner): on a low screen only the 50 px formats
  const mobile = fitting(SIZES_MOBILE, width, height) || fitting(SIZES_MOBILE, width) || SIZES_MOBILE;
  return { desktop: fitting(SIZES_DESKTOP, width, height) || mobile, mobile };
}

/** The tallest creative of the lists above that fits this screen: 50 px on a short landscape screen
 *  (legacy lbx_ads_small), else 100 px. */
export const slotHeight = (height = innerHeight): number => (height <= 500 ? 50 : 100);

/** The window's own content must keep at least this share of the window's body; below it the window first drops
 *  its secondary controls (`data-ad-tight` on the window: the Games sort row, windows.css), and only then the ad
 *  goes (owner 2026-10-26: "chowanie to ostateczność"; an ad on half the screen and a few % for the game). */
export const CONTENT_MIN_SHARE = 0.2;
/** Below this share the secondary controls go already (they cost little; the ad then keeps its place more often). */
export const CONTENT_TIGHT_SHARE = 0.33;
/** ... and never less than this much (one compact row of the Games list). */
export const CONTENT_MIN_PX = 56;
/** The rows of a window that are not its content (the ad boxes, the action rows, the Games sort row). */
const FIXED_ROWS = '.ads, .rules_buttons, .game_button_cont, .win_buttons, .games_sort';

/** The share of the window's body its content gets, with this ad box `adH` tall. */
function contentShare(win: HTMLElement, box: HTMLElement, adH: number): number {
  const body = win.querySelector<HTMLElement>('.lbx_content') ?? win;
  const total = body.clientHeight;
  if (!total) return 1;
  let fixed = 0;
  for (const el of win.querySelectorAll<HTMLElement>(FIXED_ROWS)) {
    if (el === box) fixed += adH;
    else if (el.offsetParent !== null) fixed += el.offsetHeight;
  }
  return (total - fixed) / total;
}

/** Decides, now and on every resize, whether the ad may show (see CONTENT_MIN_SHARE). */
function useAdRoom(box: React.RefObject<HTMLDivElement | null>, onChange: (show: boolean) => void) {
  useEffect(() => {
    const el = box.current;
    const win = el?.closest<HTMLElement>('.lbx_window');
    if (!el || !win) return;
    const reserve = () => slotHeight() + 18 + 12; // the creative, the vendor line, the bands and padding
    let shown: boolean | null = null;
    const check = () => {
      const adH = el.offsetHeight || reserve();
      win.removeAttribute('data-ad-tight');
      const loose = contentShare(win, el, adH);
      // the controls go first, and stay gone when the ad goes too (the room is short either way)
      if (loose < CONTENT_TIGHT_SHARE) win.setAttribute('data-ad-tight', '');
      const body = (win.querySelector<HTMLElement>('.lbx_content') ?? win).clientHeight;
      const ok = loose >= CONTENT_TIGHT_SHARE || (contentShare(win, el, adH) >= CONTENT_MIN_SHARE && contentShare(win, el, adH) * body >= CONTENT_MIN_PX);
      if (ok !== shown) {
        shown = ok;
        onChange(ok);
      }
    };
    check();
    // again once the window has opened and laid itself out (a first look before that hid the ad of the Kierki score
    // window on a phone held sideways, and nothing looked again)
    const later = [300, 900, 2000].map((ms) => window.setTimeout(check, ms));
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(() => requestAnimationFrame(check)) : null;
    ro?.observe(win);
    const body = win.querySelector('.lbx_content');
    if (body) ro?.observe(body);
    addEventListener('resize', check);
    return () => {
      later.forEach((id) => window.clearTimeout(id));
      ro?.disconnect();
      removeEventListener('resize', check);
    };
  }, [box]); // eslint-disable-line react-hooks/exhaustive-deps -- onChange is a state setter
}

/** Once Optad360 has filled the slot, the box fits the creative (owner 2026-10-17: the reserved box left
 *  grey gaps beside and under the ad): `data-filled` and `--ad-w` (the creative with its vendor line) on the
 *  box; windows.css drops the reserve and puts the ADVERTISEMENT label right above the ad's left edge.
 *  Until then the reserve keeps the window from jumping. Nothing of the creative is resized. */
function useFitToCreative(box: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    // The vendor may fill our <ins> or replace it with its own element: watch the whole box and measure
    // everything in it but an empty <ins> (the first version watched only our <ins> and never saw the
    // real Optad360 ad, 2026-10-17).
    const fit = () => {
      const kids = (Array.from(el.children) as HTMLElement[]).filter((k) => !(k.tagName === 'INS' && k.children.length === 0));
      const rects = kids.map((k) => k.getBoundingClientRect()).filter((r) => r.width > 0 && r.height > 0);
      const w = Math.max(0, ...rects.map((r) => r.width));
      if (w > 0) {
        el.dataset.filled = '';
        el.style.setProperty('--ad-w', `${Math.round(w)}px`);
      } else {
        delete el.dataset.filled;
      }
    };
    const ro = new ResizeObserver(fit);
    const mo = new MutationObserver(() => {
      for (const k of Array.from(el.children)) ro.observe(k);
      for (const ins of Array.from(el.querySelectorAll('ins'))) for (const k of Array.from(ins.children)) ro.observe(k);
      fit();
    });
    mo.observe(el, { childList: true, subtree: true });
    ro.observe(el);
    return () => {
      mo.disconnect();
      ro.disconnect();
    };
  }, [box]);
}

export function AdSlot({ slot, top = false }: { slot: 1 | 2; top?: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  useFitToCreative(box);
  // The box is measured before the <ins> goes in, because the vendor reads the size lists the moment its
  // observer sees the <ins>; they are not changed later. A plain effect, not a layout one: a state change in
  // a layout effect makes React run the window's other effects in the same task, so the Win window's first
  // counter lit up before its faded start state was ever shown (STATWIN-002).
  const [sizes, setSizes] = useState<{ desktop: string; mobile: string; h: number } | null>(null);
  // shown: the window's content keeps its room (useAdRoom); the <ins> goes in only once the ad may show (a hidden
  // ad is never requested), and stays when the ad is hidden again later (a rotation): only its box is hidden
  const [show, setShow] = useState<boolean | null>(null);
  useAdRoom(box, setShow);
  // The room saved for the ad follows the screen: a phone turned on its side while the window is open (owner
  // 2026-10-27: the Games window opened upright kept its 100 px ads sideways, one row of games left) gets a new <ins>
  // with the 50 px list (the vendor reads the list only once, and fills every new <ins>: the legacy app made a new
  // one on every open), and back. Only when the height crosses slotHeight's line, not on every resize.
  const [slotH, setSlotH] = useState(slotHeight);
  useEffect(() => {
    const onResize = () => setSlotH(slotHeight());
    addEventListener('resize', onResize);
    return () => removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    if (box.current && show && (!sizes || sizes.h !== slotH)) setSizes({ ...sizesFor(box.current.clientWidth, slotH), h: slotH });
  }, [show, sizes, slotH]);
  if (!adsEnabled()) return null;
  return (
    <div
      ref={box}
      className={top ? 'ads ads_top' : 'ads'}
      data-ad-hidden={show === false ? '' : undefined}
      style={{ '--ad-slot-h': `${slotH}px` } as CSSProperties}
    >
      {sizes ? (
        <ins
          key={sizes.h}
          id={top ? 'ads-window-win-top' : 'ads-window-games'}
          className="staticpubads89354"
          data-slot={String(slot)}
          data-sizes-desktop={sizes.desktop}
          data-sizes-mobile={sizes.mobile}
        />
      ) : null}
    </div>
  );
}
